import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  output,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ConversationHeaderComponent, ConversationListSearchBarComponent } from '../../organisms';
import { ConversationFilters } from '../../../interfaces';
import { Conversation } from '@clientwire/api';
import { FindByPipe } from '../../../pipes';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, fromEvent, map, skip, Subject, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AutoSizeVirtualScrollStrategy,
  RxVirtualScrollViewportComponent,
  ListRange,
  RxVirtualFor,
} from '@rx-angular/template/experimental/virtual-scrolling';
import { MessageComponent, Severity } from '@akostm/orbit-ui';
import {
  ActiveConversationStore,
  ConversationStore,
  ConversationTypeStore,
  ErrorStore,
} from '../../../stores';
import { ConversationHelperService, WindowService, WireApiService } from '../../../services';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'shared-conversation-list',
  standalone: true,
  imports: [
    ConversationHeaderComponent,
    ConversationListSearchBarComponent,
    FindByPipe,
    RxVirtualFor,
    RxVirtualScrollViewportComponent,
    AutoSizeVirtualScrollStrategy,
    MessageComponent,
    TranslatePipe,
  ],
  templateUrl: './conversation-list.component.html',
  styleUrl: './conversation-list.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConversationListComponent implements AfterViewInit, OnDestroy {
  conversationList = viewChild('conversationList', { read: ElementRef });
  filterInput = new FormControl('', { nonNullable: true });
  showArchived = new FormControl(false, { nonNullable: true });
  #viewRendered = false;
  #limitToHaveScrollbar = 10;

  readonly conversationsInited = output<void>();
  readonly conversations = computed(() => this.#conversationStore.entities());
  readonly activeConversationIds = computed(() => this.#activeConversationStore.ids());
  readonly conversationTypes = computed(() => this.#conversationTypeStore.types());
  readonly SeverityEnum = Severity;
  readonly refreshIcon = faRefresh;
  readonly viewsRendered$ = new Subject<Conversation[]>();
  readonly #minimumConversationSize = 58;
  readonly #conversationStore = inject(ConversationStore);
  readonly #activeConversationStore = inject(ActiveConversationStore);
  readonly #conversationTypeStore = inject(ConversationTypeStore);
  readonly #windowService = inject(WindowService);
  readonly #conversationHelperService = inject(ConversationHelperService);
  readonly #translateService = inject(TranslateService);
  readonly #errorStore = inject(ErrorStore);
  readonly #wireApiService = inject(WireApiService);
  readonly #errorLocations = ['getConversations'];
  readonly conversationErrors = computed(() =>
    this.#errorStore.getErrorsByLocations(this.#errorLocations)
  );
  #newConversationsUnsubscribe: (() => void) | null = null;
  #conversationReadStatusUnsubscribe: (() => void) | null = null;
  #conversationArchivedUnsubscribe: (() => void) | null = null;

  constructor() {
    this.viewsRendered$.pipe(take(1)).subscribe((e) => {
      this.#viewRendered = true;
      this.conversationsInited.emit();
    });
    // Input value change events
    // Skip the first value to avoid the initial value
    this.filterInput.valueChanges
      .pipe(skip(1), takeUntilDestroyed(), debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.#handleFilterChangeEvent();
      });
    // Show archived value change event
    this.showArchived.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.#handleFilterChangeEvent();
    });

    fromEvent(window, 'resize')
      .pipe(
        debounceTime(300),
        map(() => window.innerHeight),
        distinctUntilChanged(),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.#calculateLimitSize();
      });
  }

  ngAfterViewInit(): void {
    this.#calculateLimitSize();
  }

  ngOnDestroy(): void {
    this.#unsubscribeFromNewConversations();
    this.#unsubscribeFromConversationReadStatus();
    this.#unsubscribeFromConversationArchived();
    this.#conversationHelperService.unsubscribeFromConversationsUpdated();
  }

  #calculateLimitSize(): void {
    // Checking how big is the container and trying to calculate the limit with the most smallest chat messsage
    const conversationWrapperHeight = this.conversationList()?.nativeElement.offsetHeight;

    this.#limitToHaveScrollbar =
      Math.ceil(conversationWrapperHeight / this.#minimumConversationSize) + 2;
    this.#setConversationLimit(this.#limitToHaveScrollbar);

    this.#initConversations();
  }

  #handleFilterChangeEvent() {
    this.#setFilter({
      search: this.filterInput.value,
      showArchived: this.showArchived.value,
    });
  }

  isActive(id: string) {
    return this.activeConversationIds()?.includes(id);
  }

  trackByFn(index: number, item: Conversation) {
    return item.id;
  }

  rangeChanged(event: ListRange) {
    const conversations = this.conversations();
    if (this.#viewRendered && conversations && event.end >= conversations.length - 1) {
      this.#conversationShortListScrolled();
    }
  }

  refreshConversations() {
    this.#initConversations();
  }

  changeActiveState(id: string) {
    const isActive = this.#activeConversationStore.entityMap()[id];
    if (isActive) {
      this.#activeConversationStore.removeConversation(id);
      this.#conversationHelperService.unsubscribeFromConversation(id);
    } else {
      const conversation = this.#conversationStore.entityMap()[id];
      if (!conversation)
        throw new Error(
          this.#translateService.instant('SHARED.PAGES.DASHBOARD.NO_ACTIVE_CONVERSATION')
        );
      conversation.conversationData = {
        ...conversation.conversationData,
        count: 0,
      };
      this.#conversationHelperService.addToActiveConversations([conversation]);
    }
  }

  async #initConversations() {
    // Unsubscribe from events
    this.#unsubscribeFromNewConversations();
    this.#unsubscribeFromConversationReadStatus();
    this.#unsubscribeFromConversationArchived();
    this.#conversationHelperService.unsubscribeFromConversationsUpdated();
    // Get conversations
    await this.#conversationStore.getConversations(this.#windowService.conversationLimit());
    // Subscribe to events
    this.#subscribeToNewConversations();
    this.#subscribeToConversationReadStatus();
    this.#subscribeToConversationArchived();
    this.#conversationHelperService.subscribeToConversationsUpdated();
  }

  #setConversationLimit(limit: number) {
    this.#windowService.setConversationLimit(limit);
  }

  #conversationShortListScrolled() {
    if (
      !this.#conversationStore.loadingConversations() &&
      this.#conversationStore.ids().length < this.#conversationStore.totalElements()
    ) {
      this.#conversationStore.getConversations(10, this.#conversationStore.entities().length);
    }
  }

  #setFilter(newFilter: ConversationFilters) {
    this.#conversationStore.changeFilters(newFilter);
    this.#conversationStore.getConversations(this.#windowService.conversationLimit(), 0);
  }

  #subscribeToNewConversations() {
    this.#newConversationsUnsubscribe = this.#wireApiService.subscribeToNewConversation(
      (payload) => {
        this.#conversationHelperService.addToConversationListByIds([payload.conversationId]);
      }
    );
  }

  #subscribeToConversationArchived() {
    this.#conversationArchivedUnsubscribe = this.#wireApiService.subscribeToConversationArchived(
      (payload) => {
        this.#conversationHelperService.updateConversationArchived(
          payload.conversationId,
          payload.archived
        );
      }
    );
  }

  #unsubscribeFromNewConversations() {
    if (this.#newConversationsUnsubscribe) {
      this.#newConversationsUnsubscribe();
    }
  }

  #subscribeToConversationReadStatus() {
    this.#conversationReadStatusUnsubscribe =
      this.#wireApiService.subscribeToConversationReadStatus((payload) => {
        this.#conversationHelperService.updateConversationReadStatus(payload);
      });
  }

  #unsubscribeFromConversationReadStatus() {
    if (this.#conversationReadStatusUnsubscribe) {
      this.#conversationReadStatusUnsubscribe();
    }
  }

  #unsubscribeFromConversationArchived() {
    if (this.#conversationArchivedUnsubscribe) {
      this.#conversationArchivedUnsubscribe();
    }
  }
}
